var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { UserType } from "healthex-common/user.dtos";
import { action, computed, observable } from "mobx";
import { NOT_LOADED } from "../../Infra/RemoteData";
import { subDays, startOfMonth, endOfMonth, subMonths, format } from "date-fns";
const MAX_DATE_RANGE_DAYS = 366;
const DEFAULT_PAGE_SIZE = 50;
export class GlobalAdminBillingViewModel {
    constructor(container) {
        this.selectedOrganizationId = null;
        this.startDate = startOfMonth(subMonths(new Date(), 1));
        this.endDate = endOfMonth(subMonths(new Date(), 1));
        this.currentPage = 1;
        this.pageSize = DEFAULT_PAGE_SIZE;
        this.isLoadingData = false;
        this.organizationsManager = container.organizationsManager;
        this.billingManager = container.globalAdminBillingManager;
    }
    get organizations() {
        var _a, _b;
        if (this.organizationsManager.organizations.value === NOT_LOADED)
            return NOT_LOADED;
        return ((_b = (_a = this.organizationsManager.organizations.value) === null || _a === void 0 ? void 0 : _a.filter((org) => org.type === UserType.HEALTHCARE_ORGANIZATION || org.type === UserType.EXTERNAL_RESEARCHER)) !== null && _b !== void 0 ? _b : []);
    }
    get selectedOrganization() {
        var _a;
        if (this.organizations === NOT_LOADED)
            return null;
        return (_a = this.organizations.find((org) => org.id === this.selectedOrganizationId)) !== null && _a !== void 0 ? _a : null;
    }
    get billingSummary() {
        return this.billingManager.billingSummary;
    }
    get billingDetails() {
        return this.billingManager.billingDetails;
    }
    get patientResults() {
        var _a, _b;
        return (_b = (_a = this.billingDetails) === null || _a === void 0 ? void 0 : _a.results) !== null && _b !== void 0 ? _b : [];
    }
    get totalPatients() {
        var _a, _b;
        return (_b = (_a = this.billingDetails) === null || _a === void 0 ? void 0 : _a.total) !== null && _b !== void 0 ? _b : 0;
    }
    get totalPages() {
        return Math.ceil(this.totalPatients / this.pageSize);
    }
    get isLoading() {
        return this.billingManager.isLoadingDetails || this.billingManager.isLoadingSummary || this.isLoadingData;
    }
    get errorMessage() {
        return this.billingManager.errorMessage;
    }
    get dateRangeValid() {
        if (this.endDate < this.startDate)
            return false;
        const diffMs = this.endDate.getTime() - this.startDate.getTime();
        const diffDays = diffMs / (1000 * 60 * 60 * 24);
        return diffDays <= MAX_DATE_RANGE_DAYS;
    }
    get dateRangeError() {
        if (this.endDate < this.startDate) {
            return "End date must be after start date";
        }
        const diffMs = this.endDate.getTime() - this.startDate.getTime();
        const diffDays = diffMs / (1000 * 60 * 60 * 24);
        if (diffDays > MAX_DATE_RANGE_DAYS) {
            return `Date range cannot exceed ${MAX_DATE_RANGE_DAYS} days`;
        }
        return null;
    }
    get canLoadData() {
        return !!this.selectedOrganizationId && this.dateRangeValid;
    }
    setSelectedOrganization(orgId) {
        this.selectedOrganizationId = orgId;
        this.currentPage = 1;
        this.billingManager.clearData();
    }
    setStartDate(date) {
        this.startDate = date;
        this.currentPage = 1;
    }
    setEndDate(date) {
        this.endDate = date;
        this.currentPage = 1;
    }
    setPage(page) {
        this.currentPage = page;
        if (this.selectedOrganizationId) {
            void this.loadDetails();
        }
    }
    async loadData() {
        if (!this.selectedOrganizationId || !this.dateRangeValid)
            return;
        this.isLoadingData = true;
        try {
            await Promise.all([
                this.billingManager.loadBillingSummary(this.selectedOrganizationId, {
                    startDate: this.startDate,
                    endDate: this.endDate,
                }),
                this.billingManager.loadBillingDetails(this.selectedOrganizationId, {
                    startDate: this.startDate,
                    endDate: this.endDate,
                    page: this.currentPage,
                    pageSize: this.pageSize,
                }),
            ]);
        }
        finally {
            this.isLoadingData = false;
        }
    }
    async loadDetails() {
        if (!this.selectedOrganizationId || !this.dateRangeValid)
            return;
        await this.billingManager.loadBillingDetails(this.selectedOrganizationId, {
            startDate: this.startDate,
            endDate: this.endDate,
            page: this.currentPage,
            pageSize: this.pageSize,
        });
    }
    formatDate(date) {
        return format(date, "MMM d, yyyy");
    }
    formatDateRange() {
        return `${this.formatDate(this.startDate)} - ${this.formatDate(this.endDate)}`;
    }
    getOriginLabel(origin) {
        switch (origin) {
            case "PATIENT":
                return "Patient-initiated";
            case "ORGANIZATION":
                return "Organization-initiated";
            case "MCP":
                return "MCP";
            default:
                return origin;
        }
    }
    getTotalQueriesForPatient(patient) {
        return patient.billableQueriesByOrigin.reduce((sum, origin) => sum +
            origin.billableTefcaQueries +
            origin.billableFastenQueries +
            origin.billableParticleQueries +
            origin.billableOpenEpicQueries, 0);
    }
    getTotalQueriesForOrigin(origin) {
        return origin.billableTefcaQueries + origin.billableFastenQueries + origin.billableParticleQueries + origin.billableOpenEpicQueries;
    }
    async setLastMonth() {
        const lastMonth = subMonths(new Date(), 1);
        this.startDate = startOfMonth(lastMonth);
        this.endDate = endOfMonth(lastMonth);
        this.currentPage = 1;
        if (this.selectedOrganizationId) {
            await this.loadData();
        }
    }
    async setThisMonth() {
        this.startDate = startOfMonth(new Date());
        this.endDate = new Date();
        this.currentPage = 1;
        if (this.selectedOrganizationId) {
            await this.loadData();
        }
    }
    async setLast30Days() {
        this.endDate = new Date();
        this.startDate = subDays(new Date(), 30);
        this.currentPage = 1;
        if (this.selectedOrganizationId) {
            await this.loadData();
        }
    }
    async setLast90Days() {
        this.endDate = new Date();
        this.startDate = subDays(new Date(), 90);
        this.currentPage = 1;
        if (this.selectedOrganizationId) {
            await this.loadData();
        }
    }
}
__decorate([
    observable
], GlobalAdminBillingViewModel.prototype, "selectedOrganizationId", void 0);
__decorate([
    observable
], GlobalAdminBillingViewModel.prototype, "startDate", void 0);
__decorate([
    observable
], GlobalAdminBillingViewModel.prototype, "endDate", void 0);
__decorate([
    observable
], GlobalAdminBillingViewModel.prototype, "currentPage", void 0);
__decorate([
    observable
], GlobalAdminBillingViewModel.prototype, "pageSize", void 0);
__decorate([
    observable
], GlobalAdminBillingViewModel.prototype, "isLoadingData", void 0);
__decorate([
    computed
], GlobalAdminBillingViewModel.prototype, "organizations", null);
__decorate([
    computed
], GlobalAdminBillingViewModel.prototype, "selectedOrganization", null);
__decorate([
    computed
], GlobalAdminBillingViewModel.prototype, "billingSummary", null);
__decorate([
    computed
], GlobalAdminBillingViewModel.prototype, "billingDetails", null);
__decorate([
    computed
], GlobalAdminBillingViewModel.prototype, "patientResults", null);
__decorate([
    computed
], GlobalAdminBillingViewModel.prototype, "totalPatients", null);
__decorate([
    computed
], GlobalAdminBillingViewModel.prototype, "totalPages", null);
__decorate([
    computed
], GlobalAdminBillingViewModel.prototype, "isLoading", null);
__decorate([
    computed
], GlobalAdminBillingViewModel.prototype, "errorMessage", null);
__decorate([
    computed
], GlobalAdminBillingViewModel.prototype, "dateRangeValid", null);
__decorate([
    computed
], GlobalAdminBillingViewModel.prototype, "dateRangeError", null);
__decorate([
    computed
], GlobalAdminBillingViewModel.prototype, "canLoadData", null);
__decorate([
    action
], GlobalAdminBillingViewModel.prototype, "setSelectedOrganization", null);
__decorate([
    action
], GlobalAdminBillingViewModel.prototype, "setStartDate", null);
__decorate([
    action
], GlobalAdminBillingViewModel.prototype, "setEndDate", null);
__decorate([
    action
], GlobalAdminBillingViewModel.prototype, "setPage", null);
__decorate([
    action
], GlobalAdminBillingViewModel.prototype, "loadData", null);
__decorate([
    action
], GlobalAdminBillingViewModel.prototype, "loadDetails", null);
__decorate([
    action
], GlobalAdminBillingViewModel.prototype, "setLastMonth", null);
__decorate([
    action
], GlobalAdminBillingViewModel.prototype, "setThisMonth", null);
__decorate([
    action
], GlobalAdminBillingViewModel.prototype, "setLast30Days", null);
__decorate([
    action
], GlobalAdminBillingViewModel.prototype, "setLast90Days", null);
